import {
  collection,
  query,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  orderBy,
  limit,
  where,
  writeBatch,
  Unsubscribe,
  onSnapshot,
} from 'firebase/firestore';
import { getFirebaseFirestore } from '@bookstore/shared/browser';
import { COLLECTIONS, Notification } from '@bookstore/shared';

class NotificationService {
  private get db() {
    return getFirebaseFirestore();
  }

  /**
   * Get user notifications (one-time fetch)
   */
  // TODO optimize to avoid duplicates and add custom limit
  async getUserNotifications(userId: string): Promise<Notification[]> {
      // Fetch user-specific notifications
  const userQuery = query(
    collection(this.db, COLLECTIONS.NOTIFICATIONS),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

    // Fetch global notifications (targetType: 'all')
  // const globalQuery = query(
  //   collection(this.db, COLLECTIONS.NOTIFICATIONS),
  //   where('targetType', '==', 'all'),
  //   orderBy('createdAt', 'desc'),
  //   limit(50)
  // );

    // Fetch role-based notifications (targetType: 'role', role: 'customer')
  const roleQuery = query(
    collection(this.db, COLLECTIONS.NOTIFICATIONS),
    where('targetType', '==', 'role'),
    where('role', '==', 'customer'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

    // Execute all queries in parallel
  const [userSnapshot,  roleSnapshot] = await Promise.all([
    getDocs(userQuery),
    // getDocs(globalQuery),
    getDocs(roleQuery),
  ]);

     const notificationsMap = new Map<string, Notification>();

  [...userSnapshot.docs,  ...roleSnapshot.docs].forEach((doc) => {
    if (!notificationsMap.has(doc.id)) {
      notificationsMap.set(doc.id, { id: doc.id, ...doc.data() } as Notification);
    }
  });

  // Convert to array and sort by createdAt
  const notifications = Array.from(notificationsMap.values()).sort(
    (a, b) => b.createdAt.toMillis() - a.createdAt.toMillis()
  );

  return notifications.slice(0, 50); // Limit to 50 total


  }

  /**
   * Get unread count
   */
  async getUnreadCount(userId: string): Promise<number> {
    try{
      const userQuery = query(
      collection(this.db, COLLECTIONS.NOTIFICATIONS),
      where('userId', '==', userId),
      where('isRead', '==', false)
    
  );

  // Global unread
  // const globalQuery = query(
  //   collection(this.db, COLLECTIONS.NOTIFICATIONS),
  //   where('targetType', '==', 'all'),
  //   where('isRead', '==', false)
  // );

  // Role-based unread
  // const roleQuery = query(
  //   collection(this.db, COLLECTIONS.NOTIFICATIONS),
  //   where('targetType', '==', 'role'),
  //   where('role', '==', 'customer'),
  //   where('isRead', '==', false)
  // );

  const [userSnapshot] = await Promise.all([
    getDocs(userQuery),
    // getDocs(globalQuery),
    // getDocs(roleQuery),
  ]);

  // Deduplicate by ID
  const uniqueIds = new Set<string>();
  [...userSnapshot.docs].forEach((doc) => {
    uniqueIds.add(doc.id);
  });
  

  return uniqueIds.size;
    } catch(error){
      console.error("Error getting unread notification count:", error);
      throw error;  
    }
  }


  /** get unread notifications */
  async getUnreadNotifications(userId: string): Promise<Notification[]> {
// User-specific unread
  const userQuery = query(
    collection(this.db, COLLECTIONS.NOTIFICATIONS),
    where('userId', '==', userId),
    where('isRead', '==', false),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

  // // Global unread
  // const globalQuery = query(
  //   collection(this.db, COLLECTIONS.NOTIFICATIONS),
  //   where('targetType', '==', 'all'),
  //   where('isRead', '==', false),
  //   orderBy('createdAt', 'desc'),
  //   limit(50)
  // );

  // // Role-based unread
  // const roleQuery = query(
  //   collection(this.db, COLLECTIONS.NOTIFICATIONS),
  //   where('targetType', '==', 'role'),
  //   where('role', '==', 'customer'),
  //   where('isRead', '==', false),
  //   orderBy('createdAt', 'desc'),
  //   limit(50)
  // );

  const [userSnapshot] = await Promise.all([
    getDocs(userQuery),
    // getDocs(globalQuery),
    // getDocs(roleQuery),
  ]);

  // Combine and deduplicate
  const notificationsMap = new Map<string, Notification>();

  [...userSnapshot.docs].forEach((doc) => {
    if (!notificationsMap.has(doc.id)) {
      notificationsMap.set(doc.id, { id: doc.id, ...doc.data() } as Notification);
    }
  });

  // Sort and return
  return Array.from(notificationsMap.values())
    .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())
    .slice(0, 50);
  }

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<void> {
    try{
      await updateDoc(doc(this.db, COLLECTIONS.NOTIFICATIONS, notificationId), {
        isRead: true,
      });

    } catch(error){
      console.error("Error marking notification as read:", error);
      throw error;
    }
  }

  // Mark all as read
  async markAllAsRead(userId: string): Promise<void> {
// User-specific
  const userQuery = query(
    collection(this.db, COLLECTIONS.NOTIFICATIONS),
    where('userId', '==', userId),
    where('isRead', '==', false)
  );

  // Global
  const globalQuery = query(
    collection(this.db, COLLECTIONS.NOTIFICATIONS),
    where('targetType', '==', 'all'),
    where('isRead', '==', false)
  );

  // Role-based
  const roleQuery = query(
    collection(this.db, COLLECTIONS.NOTIFICATIONS),
    where('targetType', '==', 'role'),
    where('role', '==', 'customer'),
    where('isRead', '==', false)
  );

  const [userSnapshot, globalSnapshot, roleSnapshot] = await Promise.all([
    getDocs(userQuery),
    getDocs(globalQuery),
    getDocs(roleQuery),
  ]);

  const updates = [
    ...userSnapshot.docs,
    ...globalSnapshot.docs,
    ...roleSnapshot.docs,
  ].map((doc) => updateDoc(doc.ref, { isRead: true }));

  await Promise.all(updates);
  }


  /**
   * Delete notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    try{
      await deleteDoc(doc(this.db, COLLECTIONS.NOTIFICATIONS, notificationId));
    }catch(error){
      console.error("Error deleting notification:", error);
    }
  }




 /**
   * Get user notifications (real-time)
   */
  subscribeToUserNotifications(
    userId: string,
    callback: (notifications: Notification[]) => void
  ): Unsubscribe {
const notificationsMap = new Map<string, Notification>();
  let unsubscribeFns: Unsubscribe[] = [];

  const updateCallback = () => {
    const notifications = Array.from(notificationsMap.values()).sort(
      (a, b) => b.createdAt.toMillis() - a.createdAt.toMillis()
    );
    callback(notifications.slice(0, 50));
  };

  // Subscribe to user-specific notifications
  const userQuery = query(
    collection(this.db, COLLECTIONS.NOTIFICATIONS),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(50)
  );


  const userUnsubscribe = onSnapshot(userQuery, (snapshot) => {
    // Handle all changes: added, modified, removed
    snapshot.docChanges().forEach((change) => {
      if (change.type === 'added' || change.type === 'modified') {
        notificationsMap.set(change.doc.id, {
          id: change.doc.id,
          ...change.doc.data(),
        } as Notification);
      } else if (change.type === 'removed') {
        notificationsMap.delete(change.doc.id);
      }
    });
    updateCallback();
  });

  // Subscribe to global notifications
  // const globalQuery = query(
  //   collection(this.db, COLLECTIONS.NOTIFICATIONS),
  //   where('targetType', '==', 'all'),
  //   where('userId', '==', userId),
  //   orderBy('createdAt', 'desc'),
  //   limit(50)
  // );

  // const globalUnsubscribe = onSnapshot(globalQuery, (snapshot) => {
  //   // Handle all changes
  //   snapshot.docChanges().forEach((change) => {
  //     if (change.type === 'added' || change.type === 'modified') {
  //       notificationsMap.set(change.doc.id, {
  //         id: change.doc.id,
  //         ...change.doc.data(),
  //       } as Notification);
  //     } else if (change.type === 'removed') {
  //       notificationsMap.delete(change.doc.id);
  //     }
  //   });
  //   updateCallback();
  // });


  // Subscribe to role-based notifications
  // const roleQuery = query(
  //   collection(this.db, COLLECTIONS.NOTIFICATIONS),
  //   where('targetType', '==', 'role'),
  //   where('role', '==', 'customer'),
  //   where('userId', '==', userId),
  //   orderBy('createdAt', 'desc'),
  //   limit(50)
  // );

  // const roleUnsubscribe = onSnapshot(roleQuery, (snapshot) => {
  //   //  Handle all changes
  //   snapshot.docChanges().forEach((change) => {
  //     if (change.type === 'added' || change.type === 'modified') {
  //       notificationsMap.set(change.doc.id, {
  //         id: change.doc.id,
  //         ...change.doc.data(),
  //       } as Notification);
  //     } else if (change.type === 'removed') {
  //       notificationsMap.delete(change.doc.id);
  //     }
  //   });
  //   updateCallback();
  // });
  unsubscribeFns = [userUnsubscribe];

  // Return combined unsubscribe function
  return () => {
    unsubscribeFns.forEach((unsub) => unsub());
  };
  }



  /**
   * Delete all user notifications
   */
  async deleteAllUserNotifications(userId: string): Promise<void> {
    const notificationsQuery = query(
      collection(this.db, COLLECTIONS.NOTIFICATIONS),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(notificationsQuery);

    const deletes = snapshot.docs.map((doc) => deleteDoc(doc.ref));
    await Promise.all(deletes);
  }



  



}

export const notificationService = new NotificationService();